import { useEffect, useState } from 'react';

import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { diffActionFiles, type ActionFileChange } from './action-file-diff';
import { readActionFiles, readPublishedActionFiles } from './read-action-files';

export type ActionDiffState =
  | { status: 'loading'; changes: null; error: null }
  | { status: 'ready'; changes: ActionFileChange[]; error: null }
  | { status: 'error'; changes: null; error: string };

/**
 * The action code a publish changes: the draft's action scripts against the
 * version it replaces (`baselineVersion`), or everything on a first publish.
 * Re-read whenever Deploy opens or the draft's file list changes.
 */
export const useActionFileDiff = (
  baselineVersion: string | null,
  /** A published version to compare instead of the draft (promoting it). */
  targetVersion: string | null = null
): ActionDiffState => {
  const { solutionId, draftVersion, draftFiles } = useProStudioCtxSelector(
    (ctx) => ({
      solutionId: ctx.selectedBot?.uniqueId,
      draftVersion: ctx.draftVersion,
      draftFiles: (ctx.draft?.files ?? []).join('|'),
    }),
    { isEqualityDeep: true }
  );
  const [state, setState] = useState<ActionDiffState>({
    status: 'loading',
    changes: null,
    error: null,
  });

  useEffect(() => {
    if (!solutionId || !draftVersion) return undefined;
    let cancelled = false;
    setState({ status: 'loading', changes: null, error: null });
    const read = (versionName: string) => ({ solutionId, versionName });

    const before = baselineVersion
      ? readPublishedActionFiles(read(baselineVersion))
      : null;
    const after = targetVersion
      ? readPublishedActionFiles(read(targetVersion))
      : readActionFiles(read(draftVersion));

    Promise.all([before, after])
      .then(([was, now]) => {
        if (!cancelled) {
          setState({
            status: 'ready',
            changes: diffActionFiles(was, now),
            error: null,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            status: 'error',
            changes: null,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [solutionId, draftVersion, baselineVersion, targetVersion, draftFiles]);

  return state;
};
